/**
 * Regras puras do registro de telas — sem React, sem DOM (por isso têm teste: `registro.test.ts`).
 *
 * Tudo que o menu, a busca, a trilha e o "Sem acesso" precisam saber sobre uma lista de `ItemNav`
 * sai daqui, para que os cinco usos nunca divirjam.
 */
import type { GrupoNav, ItemNav } from "./tipos.js";

/** Limites do padrão A (item 4): acordeão só acima de 16 itens visíveis; busca de telas acima de 20. */
export const LIMITE_ACORDEAO = 16;
export const LIMITE_BUSCA = 20;

export function usaAcordeao(qtdItensVisiveis: number, limite = LIMITE_ACORDEAO): boolean {
  return qtdItensVisiveis > limite;
}
export function temBuscaDeTelas(qtdItensVisiveis: number, limite = LIMITE_BUSCA): boolean {
  return qtdItensVisiveis > limite;
}

/** Tira a barra final e o que vem depois de `?` ou `#` (a rota, sem a consulta). */
export function rotaLimpa(r: string): string {
  const semConsulta = r.split(/[?#]/)[0] ?? "";
  return semConsulta.length > 1 ? semConsulta.replace(/\/+$/, "") : semConsulta || "/";
}

const casaPrefixo = (prefixo: string, rota: string): boolean => {
  const p = rotaLimpa(prefixo);
  return rota === p || rota.startsWith(p + "/");
};

/**
 * Quantos caracteres da rota esta tela "ganha" (0 = não casa). O MAIOR vence: é assim que
 * `/admin/circuitos/down` acende "Circuitos fora do ar" e não "Circuitos", e que
 * `/admin/clientes/<id>` acende "Clientes" (SEN-07 e NAV-12/30).
 */
export function forcaDoCasamento(item: ItemNav, pathname: string): number {
  const rota = rotaLimpa(pathname);
  let melhor = 0;
  const href = rotaLimpa(item.href);
  if (item.exato ? rota === href : casaPrefixo(href, rota)) melhor = Math.max(melhor, href.length);
  for (const f of item.rotasFilhas ?? []) if (casaPrefixo(f, rota)) melhor = Math.max(melhor, rotaLimpa(f).length);
  for (const a of item.aliases ?? []) if (rotaLimpa(a) === rota) melhor = Math.max(melhor, rotaLimpa(a).length);
  return melhor;
}

/** A tela da rota: UMA só, a de maior prefixo (empate: a que vem primeiro no registro). */
export function itemAtivo(itens: readonly ItemNav[], pathname: string): ItemNav | null {
  let melhor: ItemNav | null = null;
  let forca = 0;
  for (const it of itens) {
    const f = forcaDoCasamento(it, pathname);
    if (f > forca) { melhor = it; forca = f; }
  }
  return melhor;
}

/** O papel abre a tela? Sem `papeis` = todos. Papel desconhecido (`null`) não abre nada que seja restrito. */
export function podeAbrir(item: ItemNav, papel: string | null | undefined): boolean {
  if (!item.papeis) return true;
  return !!papel && item.papeis.includes(papel);
}

export function itensVisiveis(itens: readonly ItemNav[], papel: string | null | undefined): ItemNav[] {
  return itens.filter((i) => podeAbrir(i, papel));
}

export interface MenuOrganizado {
  /** Itens de topo (`grupo: null`), na ordem do registro. */
  topo: ItemNav[];
  /** Só grupos com pelo menos um item visível, na ordem de `grupos`. */
  grupos: { grupo: GrupoNav; itens: ItemNav[] }[];
}

export function organizarMenu(visiveis: readonly ItemNav[], grupos: readonly GrupoNav[]): MenuOrganizado {
  const topo = visiveis.filter((i) => i.grupo === null);
  const porGrupo = grupos
    .map((g) => ({ grupo: g, itens: visiveis.filter((i) => i.grupo === g.id) }))
    .filter((g) => g.itens.length > 0);
  return { topo, grupos: porGrupo };
}

export function grupoDoItem(item: ItemNav | null, grupos: readonly GrupoNav[]): GrupoNav | null {
  if (!item || item.grupo === null) return null;
  return grupos.find((g) => g.id === item.grupo) ?? null;
}

/** "Grupo › Tela". Item de topo não tem grupo na trilha (só a tela). */
export function trilhaDe(item: ItemNav | null, grupos: readonly GrupoNav[]): { grupo?: string; tela: string } | null {
  if (!item) return null;
  const g = grupoDoItem(item, grupos);
  return g ? { grupo: g.rotulo, tela: item.rotulo } : { tela: item.rotulo };
}

export function tituloDaAba(item: ItemNav | null, sufixo: string, rotuloDesconhecido = "Tela não encontrada"): string {
  return `${item?.rotulo ?? rotuloDesconhecido} · ${sufixo}`;
}

/** Minúsculas e sem acento: "configuracoes" acha "Configurações". */
export function normalizar(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

export interface ResultadoBusca {
  item: ItemNav;
  grupo: string | null;
  /** Menor = melhor. */
  pontos: number;
}

/**
 * "Ir para…": começa-com no rótulo > contém no rótulo > sinônimo > nome do grupo. Todas as palavras
 * digitadas precisam aparecer (em qualquer campo). Termo vazio devolve tudo na ordem do registro.
 */
export function buscarTelas(itens: readonly ItemNav[], grupos: readonly GrupoNav[], termo: string, max = 12): ResultadoBusca[] {
  const palavras = normalizar(termo).split(/\s+/).filter(Boolean);
  const nomeDoGrupo = (it: ItemNav) => grupos.find((g) => g.id === it.grupo)?.rotulo ?? null;
  const res: ResultadoBusca[] = [];
  itens.forEach((item, ordem) => {
    const grupo = nomeDoGrupo(item);
    if (palavras.length === 0) { res.push({ item, grupo, pontos: ordem }); return; }
    const rot = normalizar(item.rotulo);
    const sin = (item.sinonimos ?? []).map(normalizar);
    const gru = normalizar(grupo ?? "");
    let total = 0;
    for (const p of palavras) {
      let pt: number;
      if (rot.startsWith(p) || rot.split(/\s+/).some((w) => w.startsWith(p))) pt = rot.startsWith(p) ? 0 : 1;
      else if (rot.includes(p)) pt = 2;
      else if (sin.some((s) => s.includes(p))) pt = 3;
      else if (gru.includes(p)) pt = 4;
      else return; // uma palavra não casou em lugar nenhum: fora
      total += pt;
    }
    res.push({ item, grupo, pontos: total * 1000 + ordem });
  });
  return res.sort((a, b) => a.pontos - b.pontos).slice(0, max);
}
